import { RegisterPayload } from "@shared/types";
import { AuthError, FunctionsHttpError, User as SupabaseUser } from "@supabase/supabase-js";
import { create } from "zustand";
import { supabaseClient } from "../shared/supabase";
import { useShopCartStore } from "./shopCart.store";
import * as SentryUtils from "../utils/sentry";

const siteUrl = import.meta.env.VITE_SITE_URL;

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface RegisterError {
  message: string;
  status: number;
}

export type AuthState = {
  authUser: SupabaseUser | null;
  isLoading: boolean;
  error: AuthError | null;
};

type AuthStore = {
  initialize: () => Promise<void>;
  login: (data: {
    email: string;
    password: string;
  }) => Promise<void | AuthError>;
  register: (data: RegisterPayload) => Promise<void | RegisterError>;
  logout: () => Promise<void>;
  recoverPassword: (email: string) => Promise<void | AuthError>;
  changePassword: (password: string) => Promise<void | AuthError>;
};

export const useAuthStore = create<AuthStore & AuthState>((set, get) => ({
  authUser: null,
  isLoading: true,
  error: null,

  initialize: async () => {
    if (get().authUser) return;

    set({ isLoading: true });
    const {
      data: { user },
      error,
    } = await supabaseClient.auth.getUser();

    set({
      authUser: user,
      isLoading: false,
      error,
    });

    // Set user context in Sentry if user is already authenticated
    if (user) {
      SentryUtils.setUserContext();
    }
  },

  login: async ({ email, password }) => {
    set({ isLoading: true, error: null });

    const {
      data: { user },
      error,
    } = await supabaseClient.auth.signInWithPassword({
      email,
      password,
    });

    set({
      authUser: user,
      isLoading: false,
      error: error,
    });

    if (error) {
      return error;
    }

    useShopCartStore.getState().clear();

    // Set user context in Sentry after successful login
    if (user) {
      SentryUtils.setUserContext();
    }

    return;
  },

  logout: async () => {
    try {
      await supabaseClient.auth.signOut();
    } catch (error) {
      SentryUtils.captureException(error, {
        tags: { component: "AuthStore", operation: "logout" }
      });
      console.error("Error during logout:", error);
    } finally {
      useShopCartStore.getState().clear();
    }

    // Clear user context in Sentry after logout
    SentryUtils.clearUserContext();

    set({
      authUser: null,
      isLoading: false,
      error: null,
    });
  },

  register: async (data: RegisterPayload) => {
    set({ isLoading: true, error: null });

    const { error } = await supabaseClient.functions.invoke("server/register", {
      method: "POST",
      body: data,
    });

    set({
      isLoading: false,
      error: error,
    });

    if (error) {
      // Extract status code and error message from the response
      if (error instanceof FunctionsHttpError) {
        const errorData = await error.context.json();
        const status = error.context.status;
        return {
          message: errorData.error || error.message,
          status,
        };
      }

      // Fallback for other error types
      return {
        message: error.message,
        status: 500,
      };
    }

    return;
  },

  recoverPassword: async (email) => {
    set({ isLoading: true, error: null });

    const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/change-password`,
    });

    set({
      isLoading: false,
      error: error,
    });

    if (error) {
      return error;
    }

    return;
  },

  changePassword: async (password: string) => {
    set({ isLoading: true, error: null });

    const { error } = await supabaseClient.auth.updateUser({ password });

    set({
      isLoading: false,
      error: error,
    });

    if (error) {
      return error;
    }

    return;
  },
}));
